import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import TermsModal from '../components/TermsModal';
import LocationPicker from '../components/LocationPicker';
import PHAddressSelect from '../components/PHAddressSelect';
import { resolvePHAddress } from '../utils/phAddressResolver';
import LiveQualityCamera from '../components/LiveQualityCamera';
import { useKYCWebSocket } from '../hooks/useKYCWebSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ID_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'national_id', label: 'National ID (PhilSys ID)' },
  { value: 'sss_id', label: 'SSS ID' },
  { value: 'umid', label: 'UMID' },
  { value: 'philhealth', label: 'PhilHealth ID' },
  { value: 'voters_id', label: "Voter's ID" },
  { value: 'postal_id', label: 'Postal ID' },
  { value: 'prc_id', label: 'PRC ID' }
];

import { PAYMENT_ICONS } from '../constants/paymentIcons';

const ALL_PAYMENT_METHODS = [
  { value: 'gcash', label: 'GCash', icon: PAYMENT_ICONS.gcash, configKey: 'gcash' },
  { value: 'maya', label: 'Maya', icon: PAYMENT_ICONS.maya, configKey: 'maya' },
  { value: 'bank_account', label: 'Bank Account', icon: PAYMENT_ICONS.bank_account, configKey: 'bank_transfer' }
];

const BANKS = [
  'BDO Unibank',
  'BPI (Bank of the Philippine Islands)',
  'Metrobank',
  'Land Bank of the Philippines',
  'PNB (Philippine National Bank)',
  'Security Bank',
  'Union Bank',
  'RCBC',
  'China Bank',
  'EastWest Bank',
  'Other'
];

const RegisterPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: Basic Info, 2: ID Verification, 3: Payment Method
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'client',
    date_of_birth: '',
    // Address Fields
    street_address: '',
    barangay: '',
    city: '',
    province: '',
    zip_code: '',
    location: '', // Full combined address for backward compatibility
    location_coordinates: null,
    phone: '',
    bio: '',
    skills: '',
    // Profile Photo
    avatar: null,
    // ID Verification
    id_type: '',
    id_number: '',
    id_front_image: null,
    id_back_image: null,
    selfie_with_id: null,  // New: Selfie holding the ID
    police_clearance: null,  // Barangay Clearance document (provider only)
    // Payment Method
    payment_method_type: '',
    payment_account_name: '',
    payment_account_number: '',
    payment_bank_name: '',
    // Currency preference
    currency: 'PHP'
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: false, phone: false });
  const [loading, setLoading] = useState(false);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);
  const [uploadingClearance, setUploadingClearance] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [idFrontPreview, setIdFrontPreview] = useState(null);
  const [idBackPreview, setIdBackPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [clearancePreview, setClearancePreview] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: '' });
  const [verificationStatus, setVerificationStatus] = useState(null); // Track AI verification result
  const [verifying, setVerifying] = useState(false); // Track verification in progress
  const [registeredUserId, setRegisteredUserId] = useState(null); // User ID after registration
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState(null); // Dynamic payment methods from config
  const [requireBarangayClearance, setRequireBarangayClearance] = useState(true); // Platform config
  const [showLiveCamera, setShowLiveCamera] = useState(false);

  // WebSocket connection for real-time KYC updates
  const { 
    progress: wsProgress, 
    verificationResult: wsVerificationResult,
    isConnected: wsConnected,
    reset: resetWs
  } = useKYCWebSocket(registeredUserId, verifying);

  // Fetch enabled payment methods on mount
  useEffect(() => {
    const fetchEnabledPaymentMethods = async () => {
      try {
        const response = await axios.get(`${API}/payout-methods/enabled`);
        setEnabledPaymentMethods(response.data.enabled_payout_methods);
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
        // Default to all enabled if fetch fails
        setEnabledPaymentMethods({ gcash: true, maya: true, bank_transfer: true });
      }
    };
    fetchEnabledPaymentMethods();
    // Fetch platform config for clearance requirement
    const fetchPlatformConfig = async () => {
      try {
        const res = await axios.get(`${API}/platform/config`);
        setRequireBarangayClearance(res.data.require_barangay_clearance !== false);
      } catch (err) {
        console.error('Failed to fetch platform config:', err);
      }
    };
    fetchPlatformConfig();
  }, []);

  // Filter payment methods based on what's enabled
  const availablePaymentMethods = enabledPaymentMethods 
    ? ALL_PAYMENT_METHODS.filter(method => enabledPaymentMethods[method.configKey])
    : ALL_PAYMENT_METHODS;

  // Update verification status when WebSocket receives result
  useEffect(() => {
    if (wsVerificationResult) {
      setVerificationStatus(wsVerificationResult);
      setVerifying(false);
    }
  }, [wsVerificationResult]);

  const checkUsernameAvailability = async (username) => {
    if (username.length < 3) {
      setUsernameStatus({ checking: false, available: false, message: t('register.usernameMin') });
      return;
    }
    
    setUsernameStatus({ checking: true, available: null, message: t('register.checking') });
    try {
      const response = await axios.post(`${API}/auth/check-username?username=${encodeURIComponent(username)}`);
      setUsernameStatus({ checking: false, available: response.data.available, message: response.data.message });
    } catch (err) {
      setUsernameStatus({ checking: false, available: null, message: 'Error checking username' });
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
    setFormData({ ...formData, username: value });
    if (value.length >= 3) {
      // Debounce the check
      clearTimeout(window.usernameCheckTimeout);
      window.usernameCheckTimeout = setTimeout(() => checkUsernameAvailability(value), 500);
    } else {
      setUsernameStatus({ checking: false, available: null, message: '' });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    if (e.target.name === 'email' || e.target.name === 'phone') {
      setFieldErrors((prev) => ({ ...prev, [e.target.name]: false }));
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      setError(t('register.errImageType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('register.errImageSize'));
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setAvatarPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploadingAvatar(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const response = await axios.post(`${API}/upload/avatar`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData({ ...formData, avatar: response.data.url });
      setError('');
    } catch (err) {
      setError(t('register.errUploadPhoto'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('register.errFileSize'));
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      setError(t('register.errImageType'));
      return;
    }

    // On-device quality check (blur / brightness / face) before upload
    try {
      const { analyzeImageQuality } = await import('../utils/imageQuality');
      const expectFace = type === 'selfie' || type === 'front';
      const q = await analyzeImageQuality(file, { expectFace });
      if (!q.ok && q.issues.length > 0) {
        const label = type === 'front' ? 'ID front' : type === 'back' ? 'ID back' : type === 'selfie' ? 'selfie' : 'clearance';
        const proceed = window.confirm(
          `We checked your ${label} photo and found:\n\n• ${q.issues.join('\n• ')}\n\n` +
          `Quality score: ${q.score}/100\n\n` +
          `Tap "Cancel" to retake, or "OK" to upload anyway.`
        );
        if (!proceed) {
          e.target.value = '';
          return;
        }
      }
    } catch (qErr) {
      // Quality check is best-effort; don't block upload on failure
      console.warn('Image quality check failed:', qErr);
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'front') {
        setIdFrontPreview(reader.result);
      } else if (type === 'back') {
        setIdBackPreview(reader.result);
      } else if (type === 'selfie') {
        setSelfiePreview(reader.result);
      } else if (type === 'clearance') {
        setClearancePreview(reader.result);
      }
    };
    reader.readAsDataURL(file);

    // Upload file
    let setUploading;
    if (type === 'front') setUploading = setUploadingFront;
    else if (type === 'back') setUploading = setUploadingBack;
    else if (type === 'selfie') setUploading = setUploadingSelfie;
    else if (type === 'clearance') setUploading = setUploadingClearance;
    
    setUploading(true);
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('image_type', type);
      
      const response = await axios.post(`${API}/upload/id-image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (type === 'front') {
        setFormData(prev => ({ ...prev, id_front_image: response.data.url }));
      } else if (type === 'back') {
        setFormData(prev => ({ ...prev, id_back_image: response.data.url }));
      } else if (type === 'selfie') {
        setFormData(prev => ({ ...prev, selfie_with_id: response.data.url }));
      } else if (type === 'clearance') {
        setFormData(prev => ({ ...prev, police_clearance: response.data.url }));
      }
    } catch (error) {
      setError(t('register.errUploadImage'));
    } finally {
      setUploading(false);
    }
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.email || !formData.password) {
      setError(t('register.errFillRequired'));
      return false;
    }
    if (!formData.date_of_birth) {
      setError(t('register.errDobRequired'));
      return false;
    }
    // Age validation: must be 18 or older
    const dob = new Date(formData.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    if (age < 18) {
      setError(t('register.errMin18'));
      return false;
    }
    if (!formData.phone) {
      setError(t('register.errPhoneRequired'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('register.errPassMatch'));
      return false;
    }
    if (formData.password.length < 6) {
      setError(t('register.errPassMin'));
      return false;
    }
    if (!formData.location_coordinates || formData.location_coordinates.length !== 2) {
      setError(t('register.errPinRequired', 'Please pin your exact location on the map to save your address.'));
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.id_type || !formData.id_number) {
      setError(t('register.errSelectId'));
      return false;
    }
    if (!formData.id_front_image) {
      setError(t('register.errUploadFront'));
      return false;
    }
    if (!formData.selfie_with_id) {
      setError(t('register.errUploadSelfie'));
      return false;
    }
    if (formData.role === 'provider' && requireBarangayClearance && !formData.police_clearance) {
      setError(t('register.errUploadClearance'));
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.payment_method_type || !formData.payment_account_name || !formData.payment_account_number) {
      setError(t('register.errPaymentFields'));
      return false;
    }
    if (formData.payment_method_type === 'bank_account' && !formData.payment_bank_name) {
      setError(t('register.errSelectBank'));
      return false;
    }
    return true;
  };

  const nextStep = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateStep3()) return;

    // All users must accept terms
    if (!termsAccepted) {
      setError(t('register.errAcceptTerms'));
      return;
    }

    setLoading(true);

    // Build full location string from address fields
    const locationParts = [
      formData.street_address,
      formData.barangay,
      formData.city,
      formData.province,
      formData.zip_code
    ].filter(Boolean);
    const fullLocation = locationParts.join(', ') || formData.location;

    try {
      const submitData = {
        username: formData.username,
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        location: fullLocation,
        street_address: formData.street_address,
        barangay: formData.barangay,
        city: formData.city,
        province: formData.province,
        zip_code: formData.zip_code,
        location_coordinates: formData.location_coordinates,
        phone: formData.phone,
        bio: formData.bio,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()) : [],
        avatar: formData.avatar,
        currency: formData.currency,
        id_type: formData.id_type,
        id_number: formData.id_number,
        id_front_image: formData.id_front_image,
        id_back_image: formData.id_back_image,
        selfie_with_id: formData.selfie_with_id,
        police_clearance: formData.police_clearance,
        payment_method_type: formData.payment_method_type,
        payment_account_name: formData.payment_account_name,
        payment_account_number: formData.payment_account_number,
        payment_bank_name: formData.payment_method_type === 'bank_account' ? formData.payment_bank_name : null,
        terms_accepted: termsAccepted
      };
      
      // First register the user
      const response = await axios.post(`${API}/auth/register`, submitData);
      const { user, access_token } = response.data;
      
      // Set user ID for WebSocket connection before starting verification
      setRegisteredUserId(user.id);
      
      // Then run AI KYC verification
      setVerifying(true);
      resetWs(); // Reset any previous WebSocket state
      
      let kycResponseData = null;
      try {
        const kycResponse = await axios.post(`${API}/kyc/verify`, {
          id_front_image: formData.id_front_image,
          id_back_image: formData.id_back_image,
          selfie_with_id: formData.selfie_with_id,
          police_clearance: formData.police_clearance,
          id_type: formData.id_type,
          id_number: formData.id_number
        }, {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        kycResponseData = kycResponse.data;
        
        // If WebSocket didn't already update the status, use API response
        if (!wsVerificationResult) {
          setVerificationStatus(kycResponse.data);
        }
        
        // Update user object with verification status
        user.id_verification_status = kycResponse.data.status;
        user.id_verified = kycResponse.data.verified;
      } catch (kycError) {
        console.error('KYC verification error:', kycError);
        // Don't block registration if KYC fails - user can re-verify later
        setVerificationStatus({
          status: 'needs_review',
          message: { title: 'Verification Pending', message: 'Your documents are being reviewed. You will be notified once the review is complete.' }
        });
      }
      setVerifying(false);

      // Persist token so the Try Again button can re-call /kyc/verify
      localStorage.setItem('token', access_token);

      // If verification was rejected, let the user retry without leaving the page.
      const finalStatus = wsVerificationResult?.status || verificationStatus?.status || kycResponseData?.status;
      if (finalStatus === 'rejected') {
        onLogin(user, access_token);
        return;
      }

      onLogin(user, access_token);
      navigate('/dashboard');
    } catch (error) {
      // Build the most specific error message we can from the response shape.
      let msg = 'Registration failed. Please try again.';
      try {
        const resp = error?.response;
        if (!resp) {
          msg = `Network error: ${error?.message || 'could not reach the server'}. Please check your connection and try again.`;
        } else {
          const detail = resp.data?.detail;
          if (typeof detail === 'string' && detail.trim()) {
            msg = detail;
          } else if (Array.isArray(detail) && detail.length > 0) {
            // FastAPI / Pydantic validation error: array of {loc, msg, type}
            msg = detail
              .map((d) => {
                const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
                return field ? `${field}: ${d?.msg || 'invalid value'}` : (d?.msg || 'invalid value');
              })
              .join(' • ');
          } else if (typeof resp.data === 'string' && resp.data.trim()) {
            msg = resp.data;
          } else if (resp.data?.message) {
            msg = resp.data.message;
          } else {
            msg = `Server error (HTTP ${resp.status}). Please try again or contact support.`;
          }
        }
      } catch (parseErr) {
        // fall through to default msg
      }
      console.error('Registration error:', error?.response?.status, error?.response?.data, error);
      setError(msg);
      // Surface email/phone conflicts prominently: jump back to Step 1 (where those
      // fields live), highlight the offending field, and scroll the banner into view.
      const lower = (msg || '').toLowerCase();
      const emailIssue = lower.includes('email') && (lower.includes('registered') || lower.includes('cannot be used') || lower.includes('already'));
      const phoneIssue = lower.includes('phone') && (lower.includes('registered') || lower.includes('cannot be used') || lower.includes('already'));
      if (emailIssue || phoneIssue) {
        setFieldErrors({ email: emailIssue, phone: phoneIssue });
        setStep(1);
      }
      setTimeout(() => { try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {} }, 50);
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition ${
              step >= s 
                ? 'bg-gray-900 text-white' 
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {step > s ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : s}
          </div>
          {s < 3 && (
            <div className={`w-16 h-1 mx-2 rounded ${step > s ? 'bg-gray-900' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStepLabel = () => {
    const labels = {
      1: t('register.stepBasic'),
      2: t('register.stepId'),
      3: t('register.stepPayment')
    };
    return (
      <p className="text-center text-sm text-gray-500 mb-6">
        {t('register.stepOf', { step })} <span className="font-semibold text-gray-700">{labels[step]}</span>
      </p>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4" data-testid="register-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="register-logo">
            <img 
              src="/logo-dark.png" 
              alt="GoodJobPH" 
              className="h-12 w-12 object-contain"
            />
            <span className="text-2xl font-bold text-gray-900">GoodJob</span>
          </Link>
          <h2 className="mt-8 text-3xl font-bold text-gray-900" data-testid="register-title">{t('register.createAccount')}</h2>
          <p className="mt-2 text-gray-500">{t('register.joinToday')}</p>
        </div>

        <div className="bg-white py-8 px-8 rounded-2xl shadow-sm border border-gray-100">
          {renderStepIndicator()}
          {renderStepLabel()}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 text-red-800 rounded-xl flex items-start gap-3 shadow-sm" role="alert" data-testid="register-error">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
              <div className="text-sm font-medium leading-relaxed">{error}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="register-form">
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <div data-testid="step-1">
                {/* Profile Photo Upload */}
                <div className="mb-6 text-center">
                  <label className="block text-gray-700 text-sm font-medium mb-3">
                    {t('register.profilePhoto')} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      {avatarPreview || formData.avatar ? (
                        <img
                          src={avatarPreview || (formData.avatar?.startsWith('http') ? formData.avatar : `${BACKEND_URL}${formData.avatar}`)}
                          alt="Profile Preview"
                          className="w-28 h-28 rounded-full object-cover border-4 border-gray-200 shadow-sm"
                        />
                      ) : (
                        <div className="w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center border-4 border-dashed border-gray-300">
                          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                      {uploadingAvatar && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                    <label className="mt-4 cursor-pointer bg-gray-900 text-white px-5 py-2.5 rounded-full hover:bg-gray-800 font-medium text-sm transition">
                      {formData.avatar ? t('register.changePhoto') : t('register.uploadPhoto')}
                      <input
                        type="file"
                        onChange={handleAvatarUpload}
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid="register-avatar-input"
                      />
                    </label>
                    <p className="text-xs text-gray-400 mt-2">{t('register.photoHint')}</p>
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('register.iWantTo')}</label>
                  <div className="flex gap-4">
                    <label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition ${formData.role === 'client' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="role"
                        value="client"
                        checked={formData.role === 'client'}
                        onChange={handleChange}
                        className="hidden"
                        data-testid="register-role-client"
                      />
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span className="font-medium">{t('register.hireServices')}</span>
                    </label>
                    <label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition ${formData.role === 'provider' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="role"
                        value="provider"
                        checked={formData.role === 'provider'}
                        onChange={handleChange}
                        className="hidden"
                        data-testid="register-role-provider"
                      />
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">{t('register.offerServices')}</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      {t('register.username')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleUsernameChange}
                        className={`w-full h-11 px-4 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition ${
                          usernameStatus.available === true 
                            ? 'border-green-500 focus:ring-green-500' 
                            : usernameStatus.available === false 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-200 focus:ring-gray-900 focus:border-gray-900'
                        }`}
                        placeholder={t('register.usernamePlaceholder')}
                        required
                        data-testid="register-username-input"
                      />
                      {usernameStatus.checking && (
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </span>
                      )}
                      {!usernameStatus.checking && usernameStatus.available === true && (
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">✓</span>
                      )}
                      {!usernameStatus.checking && usernameStatus.available === false && (
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500">✗</span>
                      )}
                    </div>
                    {usernameStatus.message && (
                      <p className={`text-xs mt-1 ${usernameStatus.available ? 'text-green-600' : 'text-red-600'}`}>
                        {usernameStatus.message}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">{t('register.usernameHint')}</p>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      {t('register.fullName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full h-11 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                {/* Date of Birth */}
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    {t('register.dob')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    className="w-full h-11 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                    required
                    data-testid="register-dob-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('register.dobHint')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      {t('register.email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full h-11 px-4 border rounded-xl focus:outline-none focus:ring-2 transition ${fieldErrors.email ? 'border-red-400 ring-2 ring-red-200 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 focus:ring-gray-900 focus:border-gray-900'}`}
                      required
                      data-testid="register-email-input"
                    />
                    {fieldErrors.email && (
                      <p className="mt-1 text-xs text-red-600 font-medium" data-testid="register-email-fielderror">This email is already in use or not allowed. Please use a different one.</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      {t('register.phoneNumber')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+63 XXX XXX XXXX"
                      required
                      className={`w-full h-11 px-4 border rounded-xl focus:outline-none focus:ring-2 transition ${fieldErrors.phone ? 'border-red-400 ring-2 ring-red-200 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 focus:ring-gray-900 focus:border-gray-900'}`}
                      data-testid="register-phone-input"
                    />
                    {fieldErrors.phone ? (
                      <p className="mt-1 text-xs text-red-600 font-medium" data-testid="register-phone-fielderror">This phone number is already in use or not allowed. Please use a different one.</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">{t('register.phoneOtpHint')}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      {t('register.password')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full h-11 px-4 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                        required
                        minLength="6"
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-400 hover:text-gray-600 transition"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      {t('register.confirmPassword')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full h-11 px-4 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                        required
                        data-testid="register-confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-400 hover:text-gray-600 transition"
                        data-testid="toggle-confirm-password-visibility"
                      >
                        {showConfirmPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">{t('register.zipCode')}</label>
                    <input
                      type="text"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder={t('register.zipPlaceholder')}
                      className="w-full h-11 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                      data-testid="register-zip-code-input"
                    />
                  </div>
                </div>

                {/* Address Section — pin the map FIRST so we can auto-fill the
                    Province / City / Barangay dropdowns; only the House/Bldg No.
                    & Street stays manual. */}
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('register.address')}</label>

                  {/* Step 1: Map Picker (auto-fills the address below) */}
                  <p className="text-xs text-gray-500 mb-2" data-testid="register-pin-hint">
                    {t('register.pinFirstHint', "Pin your location on the map first — we'll auto-fill your Province, City and Barangay.")}
                  </p>
                  <LocationPicker
                    onLocationSelect={async (locationData) => {
                      if (!locationData) {
                        setFormData((prev) => ({ ...prev, location: '', location_coordinates: null }));
                        return;
                      }
                      const ap = locationData.addressParts || {};
                      // Apply coords + full address immediately; pre-fill street only if empty.
                      setFormData((prev) => ({
                        ...prev,
                        location: locationData.name,
                        location_coordinates: locationData.coordinates,
                        street_address: prev.street_address || ap.street || '',
                      }));
                      // Resolve to canonical PSGC names so the dropdowns populate & cascade.
                      try {
                        const resolved = await resolvePHAddress({
                          province: ap.province,
                          city: ap.city,
                          barangay: ap.barangay,
                        });
                        setFormData((prev) => ({
                          ...prev,
                          province: resolved.province || prev.province,
                          city: resolved.city || prev.city,
                          barangay: resolved.barangay || prev.barangay,
                        }));
                      } catch (e) {
                        /* keep whatever the user already had */
                      }
                    }}
                    placeholder={t('register.mapPickPlaceholder')}
                  />

                  {/* Step 2: Cascading Province -> City/Municipality -> Barangay (auto-filled, editable) */}
                  <PHAddressSelect
                    province={formData.province}
                    city={formData.city}
                    barangay={formData.barangay}
                    onChange={({ province, city, barangay }) =>
                      setFormData((prev) => ({ ...prev, province, city, barangay }))
                    }
                    idPrefix="register"
                    className="mt-3 mb-3"
                  />

                  {/* Step 3: House/Bldg No. & Street Name — the only free-text field */}
                  <input
                    type="text"
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleChange}
                    placeholder={t('register.streetPlaceholder')}
                    className="w-full h-11 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                    data-testid="register-street-address-input"
                  />
                </div>

                {formData.role === 'provider' && (
                  <>
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        {t('register.skills')}
                      </label>
                      <input
                        type="text"
                        name="skills"
                        value={formData.skills}
                        onChange={handleChange}
                        placeholder={t('register.skillsPlaceholder')}
                        className="w-full h-11 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                        data-testid="register-skills-input"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-medium mb-2">{t('register.bio')}</label>
                      <textarea
                        name="bio"
                        value={formData.bio}
                        onChange={handleChange}
                        placeholder={t('register.bioPlaceholder')}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="register-bio-input"
                      ></textarea>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  onClick={nextStep}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-4 rounded-lg hover:shadow-lg font-semibold transition"
                  data-testid="next-step-button"
                >
                  {t('register.nextIdVerification')}
                </button>
              </div>
            )}

            {/* Step 2: ID Verification */}
            {step === 2 && (
              <div data-testid="step-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-blue-800 mb-2">{t('register.whyId')}</h4>
                  <p className="text-sm text-blue-700">
                    {t('register.whyIdDesc')}
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    {t('register.govIdType')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="id_type"
                    value={formData.id_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="id-type-select"
                  >
                    <option value="">{t('register.selectIdType')}</option>
                    {ID_TYPES.map(id => (
                      <option key={id.value} value={id.value}>{id.label}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    {t('register.idNumber')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="id_number"
                    value={formData.id_number}
                    onChange={handleChange}
                    placeholder={t('register.idNumberPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="id-number-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Front of ID */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      {t('register.frontId')} <span className="text-red-500">*</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition">
                      {idFrontPreview ? (
                        <div className="relative">
                          <img 
                            src={idFrontPreview} 
                            alt="ID Front" 
                            className="max-h-40 mx-auto rounded"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setIdFrontPreview(null);
                              setFormData({ ...formData, id_front_image: null });
                            }}
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <div className="text-gray-500">
                            {uploadingFront ? (
                              <span>{t('register.uploading')}</span>
                            ) : (
                              <>
                                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <p className="mt-1 text-sm">{t('register.uploadFront')}</p>
                                <p className="text-xs text-gray-400">{t('register.fileHint')}</p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => handleFileUpload(e, 'front')}
                            className="hidden"
                            data-testid="id-front-upload"
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Back of ID */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      {t('register.backId')}
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition">
                      {idBackPreview ? (
                        <div className="relative">
                          <img 
                            src={idBackPreview} 
                            alt="ID Back" 
                            className="max-h-40 mx-auto rounded"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setIdBackPreview(null);
                              setFormData({ ...formData, id_back_image: null });
                            }}
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <div className="text-gray-500">
                            {uploadingBack ? (
                              <span>{t('register.uploading')}</span>
                            ) : (
                              <>
                                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <p className="mt-1 text-sm">{t('register.uploadBack')}</p>
                                <p className="text-xs text-gray-400">{t('register.fileHint')}</p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => handleFileUpload(e, 'back')}
                            className="hidden"
                            data-testid="id-back-upload"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {/* Selfie with ID */}
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    {t('register.selfieId')} <span className="text-red-500">*</span>
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    {t('register.selfieDesc')}
                  </p>
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={() => setShowLiveCamera(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full text-sm font-semibold hover:shadow-lg transition"
                      data-testid="open-live-camera-btn"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {t('register.useLiveCamera')}
                    </button>
                    <p className="text-xs text-gray-400 mt-1">{t('register.liveCameraHint')}</p>
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition">
                    {selfiePreview ? (
                      <div className="relative">
                        <img 
                          src={selfiePreview} 
                          alt="Selfie with ID" 
                          className="max-h-48 mx-auto rounded"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setSelfiePreview(null);
                            setFormData(prev => ({ ...prev, selfie_with_id: null }));
                          }}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <div className="text-gray-500">
                          {uploadingSelfie ? (
                            <span>{t('register.uploading')}</span>
                          ) : (
                            <>
                              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <p className="mt-1 text-sm">{t('register.uploadSelfie')}</p>
                              <p className="text-xs text-gray-400">{t('register.fileHint')}</p>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(e) => handleFileUpload(e, 'selfie')}
                          className="hidden"
                          data-testid="selfie-upload"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Barangay Clearance - Provider only, when config requires it */}
                {formData.role === 'provider' && requireBarangayClearance && (
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    {t('register.barangayClearance')} <span className="text-red-500">*</span>
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    {t('register.clearanceDesc')}
                  </p>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition">
                    {clearancePreview ? (
                      <div className="relative">
                        <img 
                          src={clearancePreview} 
                          alt="Barangay Clearance" 
                          className="max-h-48 mx-auto rounded"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setClearancePreview(null);
                            setFormData({ ...formData, police_clearance: null });
                          }}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <div className="text-gray-500">
                          {uploadingClearance ? (
                            <span>{t('register.uploading')}</span>
                          ) : (
                            <>
                              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <p className="mt-1 text-sm">{t('register.uploadClearance')}</p>
                              <p className="text-xs text-gray-400">{t('register.fileHint')}</p>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(e) => handleFileUpload(e, 'clearance')}
                          className="hidden"
                          data-testid="clearance-upload"
                        />
                      </label>
                    )}
                  </div>
                </div>
                )}

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex-1 h-12 border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition"
                    data-testid="prev-step-button"
                  >
                    {t('register.back')}
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex-1 h-12 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium transition"
                    data-testid="next-step-button"
                  >
                    {t('register.nextPayment')}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Payment Method */}
            {step === 3 && (
              <div data-testid="step-3">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('register.paymentBilling')}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {formData.role === 'provider' 
                      ? t('register.paymentDescProvider')
                      : t('register.paymentDescClient')}
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-3">
                    {t('register.selectPayment')} <span className="text-red-500">*</span>
                  </label>
                  {availablePaymentMethods.length === 0 ? (
                    <div className="text-center p-4 bg-gray-50 rounded-xl text-gray-500">
                      {t('register.noPaymentMethods')}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {availablePaymentMethods.map(method => (
                        <label
                          key={method.value}
                          className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition ${
                            formData.payment_method_type === method.value
                              ? 'border-gray-900 bg-gray-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="payment_method_type"
                            value={method.value}
                            checked={formData.payment_method_type === method.value}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <img src={method.icon} alt={method.label} className="w-8 h-8 object-contain rounded mr-2" />
                          <span className="font-semibold">{method.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {formData.payment_method_type && (
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        {t('register.accountName')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="payment_account_name"
                        value={formData.payment_account_name}
                        onChange={handleChange}
                        placeholder={t('register.accountNamePlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="payment-account-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        {formData.payment_method_type === 'bank_account' ? t('register.accountNumber') : t('register.mobileNumber')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="payment_account_number"
                        value={formData.payment_account_number}
                        onChange={handleChange}
                        placeholder={formData.payment_method_type === 'bank_account' ? t('register.accountNumberPlaceholder') : t('register.mobilePlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="payment-account-number-input"
                      />
                    </div>

                    {formData.payment_method_type === 'bank_account' && (
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          {t('register.bankName')} <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="payment_bank_name"
                          value={formData.payment_bank_name}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="payment-bank-select"
                        >
                          <option value="">{t('register.selectBank')}</option>
                          {BANKS.map(bank => (
                            <option key={bank} value={bank}>{bank}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Currency Preference */}
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    {t('register.currencyPref')}
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    {t('register.currencyDesc')}
                  </p>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                    data-testid="currency-select"
                  >
                    <option value="PHP">PHP - Philippine Peso</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="SGD">SGD - Singapore Dollar</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                  </select>
                </div>

                {/* Terms and Conditions for All Users */}
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                      data-testid="terms-checkbox-register"
                    />
                    <div>
                      <p className="text-gray-700 text-sm">
                        {t('register.agreeToTerms')}{' '}
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="text-blue-600 hover:text-blue-700 font-semibold underline"
                        >
                          {t('register.termsLink')}
                        </button>
                        {' '}{t('register.ofGoodjob')}
                      </p>
                      <p className="text-amber-600 text-xs mt-1 font-medium">
                        {t('register.requiredCreate')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex-1 h-12 border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition"
                    data-testid="prev-step-button"
                  >
                    {t('register.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading || verifying || !termsAccepted}
                    className={`flex-1 h-12 rounded-full font-medium transition ${
                      loading || verifying || !termsAccepted
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                    data-testid="register-submit-button"
                  >
                    {loading ? t('register.creatingAccount') : verifying ? t('register.verifyingIdentity') : t('register.createAccountBtn')}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Real-time Progress Indicator */}
          {verifying && wsProgress && (
            <div className="mt-6 p-4 rounded-xl bg-blue-50 border border-blue-200" data-testid="verification-progress">
              <div className="flex items-center gap-3 mb-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <h4 className="font-semibold text-blue-800">
                  {wsProgress.stepLabels?.[wsProgress.step] || t('register.processing')}
                </h4>
                {wsConnected && (
                  <span className="ml-auto text-xs text-blue-500 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    {t('register.live')}
                  </span>
                )}
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${wsProgress.progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-blue-600 mt-2 text-right">{t('register.percentComplete', { percent: wsProgress.progress })}</p>
            </div>
          )}

          {/* Verification Status Message */}
          {verificationStatus && (
            <div className={`mt-6 p-4 rounded-xl ${
              verificationStatus.status === 'approved' ? 'bg-green-50 border border-green-200' :
              verificationStatus.status === 'rejected' ? 'bg-red-50 border border-red-200' :
              'bg-yellow-50 border border-yellow-200'
            }`} data-testid="verification-status">
              <div className="flex items-start gap-3">
                {verificationStatus.status === 'approved' ? (
                  <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : verificationStatus.status === 'rejected' ? (
                  <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <div>
                  <h4 className={`font-semibold ${
                    verificationStatus.status === 'approved' ? 'text-green-800' :
                    verificationStatus.status === 'rejected' ? 'text-red-800' :
                    'text-yellow-800'
                  }`}>
                    {verificationStatus.message?.title || t('register.verificationStatus')}
                  </h4>
                  <p className={`text-sm mt-1 ${
                    verificationStatus.status === 'approved' ? 'text-green-700' :
                    verificationStatus.status === 'rejected' ? 'text-red-700' :
                    'text-yellow-700'
                  }`}>
                    {verificationStatus.message?.message || t('register.processing')}
                  </p>
                  {verificationStatus.rejection_reasons && verificationStatus.rejection_reasons.length > 0 && (
                    <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                      {verificationStatus.rejection_reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  )}
                  {verificationStatus.status === 'rejected' && (
                    <div className="mt-4 flex flex-wrap gap-3" data-testid="register-kyc-retry-row">
                      <button
                        type="button"
                        disabled={verifying}
                        onClick={async () => {
                          try {
                            setVerifying(true);
                            const token = localStorage.getItem('token');
                            const resp = await axios.post(`${API}/kyc/verify`, {
                              id_front_image: formData.id_front_image,
                              id_back_image: formData.id_back_image,
                              selfie_with_id: formData.selfie_with_id,
                              police_clearance: formData.police_clearance,
                              id_type: formData.id_type,
                              id_number: formData.id_number,
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            setVerificationStatus(resp.data);
                            if (resp.data?.status === 'approved') {
                              setTimeout(() => navigate('/dashboard'), 1200);
                            }
                          } catch (err) {
                            setError(err.response?.data?.detail || 'Retry failed');
                          } finally {
                            setVerifying(false);
                          }
                        }}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 transition flex items-center gap-2"
                        data-testid="register-try-again-ai-btn"
                      >
                        {verifying ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                            {t('register.verifying')}
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            {t('register.tryAgainAi')}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        className="border border-gray-300 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-50 font-semibold transition"
                        data-testid="register-continue-anyway-btn"
                      >
                        {t('register.continueAnyway')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-500">
              {t('register.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-gray-900 hover:text-gray-700 font-semibold" data-testid="register-login-link">
                {t('register.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => setTermsAccepted(true)}
      />

      {/* Live Quality Camera Modal */}
      {showLiveCamera && (
        <LiveQualityCamera
          facingMode="user"
          expectFace
          title={t('register.takeSelfieTitle')}
          onCancel={() => setShowLiveCamera(false)}
          onCapture={async (file) => {
            setShowLiveCamera(false);
            // Reuse existing upload handler via a synthetic event
            const syntheticEvent = { target: { files: [file], value: '' } };
            await handleFileUpload(syntheticEvent, 'selfie');
          }}
        />
      )}
    </div>
  );
};

export default RegisterPage;
